import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, List, Grid, ListItem, Typography, ListItemText, Button } from '@mui/material';
import { Card } from 'components/UI';
import i18n from 'i18n-js';
import Can from 'components/Can';
import { creditTypesActions } from 'state/modules/creditTypes';
import { formatAmount } from 'utils';
import { CreateCreditTypeModal } from 'components/Modals';
import PropTypes from 'prop-types';

const nameSx = (theme) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  color: theme.palette.text.primary,
});

const promoCodeListSx = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  width: '100%',
  marginTop: 2,
  alignItems: 'flex-start',
};

const promoCodeListItemSx = (theme) => ({
  width: '100%',
  padding: '0 4px 8px 0',
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
});

const promoCodeLinkSx = {
  fontSize: 12,
  fontWeight: 500,
  textTransform: 'capitalize',
};

const textRightSx = {
  textAlign: 'right',
};

const listTextRootSx = {
  display: 'flex',
  justifyContent: 'space-between',
};

const listTextPrimarySx = (theme) => ({
  color: theme.palette.text.secondary,
  fontSize: '13px',
  fontWeight: 500,
  wordBreak: 'break-word',
});

const listTextSecondarySx = (theme) => ({
  color: theme.palette.grey[700],
  fontSize: '13px',
  wordBreak: 'break-word',
});

const CreditTypeDetailsWidget = ({ id }) => {
  const dispatch = useDispatch();
  const creditType = useSelector((state) => state.creditTypes.creditType);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchCreditType = useCallback(
    (id) => {
      dispatch(creditTypesActions.fetchCreditTypeById(id));
    },
    [dispatch]
  );

  const toggleModal = () => {
    setShowCreateModal((state) => !state);
  };

  useEffect(() => {
    fetchCreditType(id);
  }, [fetchCreditType, id]);

  /**
   * Parse credit type data
   * @param {object} creditType Object with credit type data
   */
  const getCreditTypeInfo = (creditType = {}) => [
    {
      label: `${i18n.t(`common.type`)}:`,
      value: `${creditType?.accounting_type?.name || '-'}`,
    },
    {
      label: `${i18n.t(`common.name`)}:`,
      value: `${creditType?.name || '-'}`,
    },
    {
      label: `${i18n.t(`common.default_price`)}:`,
      value: `$${formatAmount(creditType?.default_unit_price || 0)}`,
    },
    {
      label: `${i18n.t(`common.description`)}:`,
      value: creditType?.description || '-',
      fullWidth: true,
    },
  ];

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];
  /**
   * Open update promo code modal
   */

  return (
    <Card
      menuItems={items}
      menuPermissions={{
        action: 'update',
        subject: 'invoice_item_type',
        type: 'can',
      }}
      title={i18n.t('credit_types.info')}
      headerActionType="vertical"
      contentRootSx={{ padding: 0, paddingBottom: 0 }}
    >
      <Box>
        <Grid container direction="row" alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h3">
              <Box sx={nameSx}>ID# {id}</Box>
            </Typography>
          </Grid>
          <Grid item sx={textRightSx}>
            <Can I="update" a="invoice_item_type">
              <Typography>
                <Button
                  onClick={toggleModal}
                  data-cy="update-info"
                  sx={promoCodeLinkSx}
                  color="primary"
                >
                  {i18n.t('common.update_information')}
                </Button>
              </Typography>
            </Can>
          </Grid>
        </Grid>

        <List sx={promoCodeListSx}>
          {getCreditTypeInfo(creditType).map((val, index) => (
            <ListItem
              key={index}
              sx={(theme) => ({
                ...promoCodeListItemSx(theme),
              })}
            >
              <ListItemText
                sx={listTextRootSx}
                primary={
                  <Typography sx={listTextPrimarySx} component="span">
                    {val.label}
                  </Typography>
                }
                secondary={
                  <Box
                    display="flex"
                    component="span"
                    flexDirection="column"
                    sx={!val.fullWidth ? textRightSx : undefined}
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      color="textSecondary"
                      sx={listTextSecondarySx}
                    >
                      {val.value}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
        <CreateCreditTypeModal id={id} open={showCreateModal} toggleModal={toggleModal} />
      </Box>
    </Card>
  );
};

CreditTypeDetailsWidget.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export { CreditTypeDetailsWidget };
